// Training Center content — all modules, sections, dropdowns, fields explained.
// Each module is a self-contained guide that maps to the platform's sidebar.

export const TRAINING_GROUPS = [
  {
    group: "Core",
    modules: [
      "dashboard", "sales", "my-rollup", "hierarchy",
    ],
  },
  {
    group: "Loyalty Engine",
    modules: ["wallets", "milestones", "referrals"],
  },
  {
    group: "Network",
    modules: ["physios", "products"],
  },
  {
    group: "Engagement",
    modules: ["rewards", "cohorts", "campaigns", "whatsapp"],
  },
  {
    group: "Operations",
    modules: ["inventory", "fulfillment", "alerts", "grievances", "audit"],
  },
  {
    group: "Administration",
    modules: ["admin-imports", "users", "tenants", "config"],
  },
];

// Helper to keep section objects short & consistent.
const sec = (title, body, items = []) => ({ title, body, items });

export const TRAINING_MODULES = {
  /* ====================== CORE ====================== */
  "dashboard": {
    title: "Executive Dashboard",
    route: "/dashboard",
    screenshot: "dashboard.png",
    purpose: "The home page for HQ leadership. A single-glance command center that summarises programme health across all territories — points in circulation, active physios, fulfillment SLA, campaign reach and AI-flagged anomalies.",
    audience: "Super Admin, HO Admin, National / Zonal / Regional Manager. DSE sees a scoped version.",
    sections: [
      sec("KPI Strip (top row)", "Six headline metrics calculated live from MongoDB aggregations.", [
        "Active Physios — count of physios with status = active in the selected tenant.",
        "Points Issued (this quarter) — sum of `points` across all credit transactions within the current quarter.",
        "Points Redeemed — sum of `points` across redeem transactions.",
        "Average Order Value — total order value / number of distinct orders for the period.",
        "Active Milestones — physios who have reached at least one milestone slab.",
        "Fulfillment SLA — percentage of redemptions delivered within the configured SLA (default 7 days).",
      ]),
      sec("Quarter Selector", "Drop-down at the top right that scopes the entire page to Q1 / Q2 / Q3 / Q4. Aggregations rerun on the server using the chosen quarter's date range.", []),
      sec("Trend Panel", "12-week sparkline of order volume vs. activation rate. Hover any point to see the exact value and week label.", []),
      sec("AI Risk Cards", "Surfaces three machine-flagged anomalies — e.g. a sudden drop in DSE order rate, churn risk in a region, or unusual redemption velocity. Click any card to open the full AI Insights page filtered to that anomaly.", []),
      sec("Territory Heat Strip", "Mini-bar of points-per-physio by zone. Use it to spot under-performing zones at a glance.", []),
    ],
    tips: [
      "Numbers are real-time; if you wipe data they will fall to zero — that is expected.",
      "Switch tenant from the top-bar tenant pill (only Super Admin) to see another brand's data.",
    ],
  },

  "sales": {
    title: "Sales / DSE Dashboard",
    route: "/sales",
    screenshot: "sales.png",
    purpose: "A field-team focused view that helps a DSE (or DSE's manager) understand their pipeline, daily call list and conversion KPIs.",
    audience: "Primary: DSE. Managers see a roll-up of their direct reports.",
    sections: [
      sec("My Targets", "Quarterly point and order targets vs. achieved. Progress bar turns amber after 60% and green after 100%.", []),
      sec("Today's Action List", "AI-generated list of the top 10 physios the DSE should contact today, ranked by score: order recency, milestone proximity and redemption likelihood.", []),
      sec("Recent Activity", "Last 20 events from your physio book — orders placed, points credited, reward picked, grievance raised.", []),
      sec("Conversion Funnel", "Visits → Orders → Repeat → Milestone hit. Each step shows count and drop-off %.", []),
    ],
    tips: ["The 'Call Now' button next to each physio opens a WhatsApp prefilled message if WhatsApp is set up."],
  },

  "my-rollup": {
    title: "My Territory Rollup",
    route: "/my-rollup",
    screenshot: "my-rollup.png",
    purpose: "Shows the live performance of every direct + indirect report below your hierarchy node. Lets a Zonal/Regional Manager compare DSEs across their patch.",
    audience: "All managerial roles. DSEs see only their own row.",
    sections: [
      sec("Hierarchy Tree", "Left pane shows your sub-tree. Click any node to refocus the table on that branch.", []),
      sec("Performance Table", "Columns: Name, ERP Id, Active Physios, Points Issued, Redeemed, AOV, Last Activity. Click column header to sort.", []),
      sec("Date Range", "Top-right date picker. All metrics recompute when you change the range.", []),
      sec("Export", "CSV export of the visible table. Honours your current filter and date range.", []),
    ],
    tips: ["A red dot next to 'Last Activity' = no order in 14 days. Treat that as a churn signal."],
  },

  "hierarchy": {
    title: "Territory & Hierarchy",
    route: "/hierarchy",
    screenshot: "hierarchy.png",
    purpose: "Visualise and edit the org chart: National → Zonal → Regional → DSE → Physio. The tree is the spine of every data filter in the platform.",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("Tree View", "Collapsible tree. Each node shows: role, name, ERP Id and a small badge with the count of physios under them.", []),
      sec("Drag-to-Reassign", "Drag a DSE node onto a different Regional Manager to reassign the entire physio book to the new manager. A confirm modal appears before the change is committed.", []),
      sec("Add Node", "+ button on any node to add a child user. Opens a side panel asking for: name, email, role (auto-restricted to allowed child roles), territory label.", []),
      sec("Search", "Top-bar search filters the tree to matching nodes (name / ERP Id / territory).", []),
    ],
    tips: ["Re-assignments are audited — see Compliance & Audit → 'Hierarchy Change' events for who moved whom and when."],
  },

  "ai-insights": {
    title: "AI Insights",
    route: "/ai-insights",
    screenshot: "ai-insights.png",
    purpose: "A library of AI-generated insights — anomaly detection, cohort risk, churn prediction, recommended next-best-action — backed by an LLM that you can also chat with.",
    audience: "All roles. DSE sees scope-limited insights.",
    sections: [
      sec("Insight Cards", "Each card has: a one-line summary, the affected entities, severity (low/medium/high), and a 'Take Action' button.", []),
      sec("Severity Filter", "Drop-down: All / High / Medium / Low. Defaults to All.", []),
      sec("Category Filter", "Churn Risk · Order Anomaly · Reward Velocity · Hierarchy Gap · Compliance.", []),
      sec("Chat with CBC Assistant", "Top-right button. Opens the contextual assistant — ask 'why is Mumbai down this week?' and it queries the database and explains.", []),
    ],
    tips: ["The Assistant uses the Emergent Universal LLM key — keep prompts short for faster, cheaper answers."],
  },

  /* =================== LOYALTY ENGINE =================== */
  "wallets": {
    title: "Physio Report",
    route: "/wallets",
    screenshot: "wallets.png",
    purpose: "Per-physio wallet and milestone report — search a physio by name, code, mobile or city to inspect their Transaction wallet (Pending / Active / Redeemed / Expired / Reversed) and their rolling Milestone wallets (Quarterly · Annual).",
    audience: "All admin roles. DSE can view only their physios.",
    sections: [
      sec("Search box (left rail)", "Type any part of name, physio code, mobile, city, state, or ERP outlet id. The list filters live as you type. Counter shows matched / total.", []),
      sec("Physio list (left rail)", "Click any row to load that physio's wallet on the right. Selected row turns teal.", []),
      sec("Transaction Wallet (top)", "Five state pills covering the full points lifecycle.", [
        "Pending — points credited by Field Assist order webhook but awaiting delivery confirmation.",
        "Active — points unlocked after delivery confirmation; available for redemption.",
        "Redeemed — points already used to claim a reward.",
        "Expired — quarterly points that lapsed at quarter rollover.",
        "Reversed — points clawed back due to order cancellation or fraud check.",
      ]),
      sec("Milestone Wallets", "Two rolling progress bars — Quarterly (resets each quarter) and Annual (FY April → March).", []),
    ],
    tips: [
      "Points lifecycle is fully driven by Field Assist `/api/orders` and `/api/orders/deliver` webhooks. There is no manual order entry in this view.",
      "If a physio's Active balance looks lower than expected, check their Redeemed column — it likely went into a recent reward redemption.",
    ],
  },

  "milestones": {
    title: "Milestone Engine",
    route: "/milestones",
    screenshot: "milestones.png",
    purpose: "Define the slabs that unlock bonus rewards as a physio's points cross thresholds during a specific window (Q1 / Q2 / Q3 / Q4 / Annual).",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("Slab Groups by Window", "Cards are grouped under five sections: Q1 (Apr-Jun), Q2 (Jul-Sep), Q3 (Oct-Dec), Q4 (Jan-Mar) and Annual (FY Apr-Mar). Empty quarter sections are hidden unless they are the current quarter.", []),
      sec("Add / Edit Slab", "Modal fields: Window (Q1 / Q2 / Q3 / Q4 / Annual drop-down), Name, Threshold points, Reward label, Bonus points.", []),
      sec("Window drop-down", "Picks which evaluation bucket the slab belongs to.", [
        "Q1, Q2, Q3, Q4 — evaluate against the physio's `quarterly_points` during that specific quarter only.",
        "Annual — evaluates against the physio's `annual_points` across the whole financial year.",
      ]),
      sec("Recent Milestone Unlocks", "Sortable, filterable ledger of every slab a physio has crossed. Columns: Date, Physio, Window, Reward, Bonus, Status (unlocked / in_transit / delivered).", []),
      sec("Delete Slab", "Trash icon on any slab card. The slab stops awarding bonuses but historical unlocks are preserved.", []),
    ],
    tips: ["Use Annual slabs for premium-tier rewards (international vacations, electronics) and quarter slabs for smaller seasonal boosters."],
  },

  "referrals": {
    title: "Referral Engine",
    route: "/referrals",
    screenshot: "referrals.png",
    purpose: "Track physio-to-physio referrals: who invited whom, who has converted, and how many points have been awarded.",
    audience: "All admin roles.",
    sections: [
      sec("Referrals Table", "Columns: Inviter, Invitee, Channel (WhatsApp/QR), Date Invited, Date Activated, Status (Pending / Activated / Expired), Points Awarded.", []),
      sec("Status Filter", "Drop-down: All / Pending / Activated / Expired (90+ days no activity).", []),
      sec("Referral Reward Config", "Top-right gear icon. Edit: inviter reward (points), invitee reward (points), validity (days).", []),
      sec("Top Referrers", "Side panel showing the top 10 physios by successful referrals — useful for marketing case studies.", []),
    ],
    tips: ["Referrals are credited only after the invitee places their first delivered order."],
  },

  /* ===================== NETWORK ===================== */
  "physios": {
    title: "Physio Master",
    route: "/physios",
    screenshot: "physios.png",
    purpose: "The master database of every physio on the programme. Search, filter, edit, and inspect any physio's profile and history.",
    audience: "All roles. DSE sees only their assigned physios.",
    sections: [
      sec("Data Table", "Columns: Physio Id, Name, Mobile, City, State, ERP Outlet Id, DSE, Status, Last Order. Click a row to open the detail drawer.", []),
      sec("Filters (top bar)", "State, City, Status (Active / Inactive / Pending KYC), DSE, Last-Order date range.", []),
      sec("Search", "Free-text — matches name, mobile, ERP id.", []),
      sec("Add Physio", "Manual entry. Captures: Name, Mobile (10-digit), Reception number (optional), City, State, PIN, ERP Outlet Id, assigned DSE.", []),
      sec("Detail Drawer", "Profile + tabs: Wallets · Orders · Redemptions · Activity · Documents.", []),
      sec("Bulk Upload", "Use Data Imports module — see that section.", []),
      sec("Export", "CSV of currently filtered set.", []),
    ],
    tips: [
      "Mobile vs Reception number — 'Mobile' is the primary WhatsApp number, 'Reception' is the clinic landline.",
      "If you can't see physios after a bulk upload, double-check that the tenant_id in the file matches your logged-in tenant.",
    ],
  },

  "onboarding": {
    title: "WhatsApp Onboarding",
    route: "/onboarding",
    screenshot: "onboarding.png",
    purpose: "Generate a WhatsApp deep-link or QR code that a physio scans to self-onboard. Captures KYC fields via a chat flow.",
    audience: "DSE and above.",
    sections: [
      sec("Generate Link", "Form: DSE assignment, City, State, optional pre-fill payload. On submit, a QR + short URL is generated.", []),
      sec("Active Links", "Table of live onboarding links with: created by, scans, completions, last activity.", []),
      sec("Link Status", "Toggle a link to Pause / Archive. Paused links continue to load but show 'Closed' message.", []),
    ],
    tips: ["Print the QR or share the link in a WhatsApp broadcast — both flows funnel into the same Approvals queue."],
  },

  "approvals": {
    title: "Onboarding Approvals",
    route: "/approvals",
    screenshot: "approvals.png",
    purpose: "A workqueue for approving (or rejecting) physios who self-onboarded via WhatsApp. Compliance step before they can earn points.",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("Pending Queue", "Table of physios awaiting review. Click a row to see the WhatsApp-captured details + uploaded ID proof.", []),
      sec("Approve / Reject Buttons", "Approve sets status to active. Reject sets to rejected and triggers a WhatsApp notice to the physio with a reason.", []),
      sec("Bulk Approve", "Tick multiple rows → 'Approve Selected'. Restricted to HO Admin and above.", []),
      sec("Reject Reason Drop-down", "Duplicate · Invalid Mobile · Failed KYC · Out of Territory · Other.", []),
    ],
    tips: ["Always check for duplicate mobile numbers — the system flags exact matches but not typos."],
  },

  "products": {
    title: "Product Catalog",
    route: "/products",
    screenshot: "products.png",
    purpose: "Maintain the list of Zixa Strong SKUs that earn points when ordered. The catalog drives points calculation on every order webhook from Field Assist.",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("SKU Table", "Columns: SKU code, Name, MRP, Points-per-rupee (override), Status.", []),
      sec("Add SKU", "Modal: SKU code (unique), Name, MRP, Default points-per-rupee (leave blank to use programme default), Status.", []),
      sec("Edit / Pause", "Pause an SKU to stop accruing points without deleting historical orders.", []),
      sec("Bulk Upload", "CSV import — same shape as the form fields.", []),
    ],
    tips: ["Catalog changes are versioned — historical orders always use the points-per-rupee that was active on the order date."],
  },

  /* =================== ENGAGEMENT =================== */
  "rewards": {
    title: "Reward Catalog",
    route: "/rewards",
    screenshot: "rewards.png",
    purpose: "Curate the gifts and vouchers physios can redeem with their points. Manage stock, points cost, eligibility tier and seasonal availability.",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("Reward Card Grid", "Each card shows image, name, points cost, tier, in-stock count, redemption count.", []),
      sec("Add Reward", "Modal: Name, Description, Points cost, Tier (Bronze/Silver/Gold/Platinum), Image upload, Stock, Reservation expiry (days), Active toggle.", []),
      sec("Tier Filter", "Drop-down: All / Bronze / Silver / Gold / Platinum. Tiers gate which physios can see/pick the reward.", []),
      sec("Stock Threshold Alerts", "Red badge appears when stock is below the configured threshold (default 10).", []),
      sec("Reservation Engine", "When a physio picks a reward via WhatsApp, stock is reserved for N days (configured per reward) before it's either redeemed (delivered) or released.", []),
    ],
    tips: ["Don't deactivate a reward with outstanding reservations — release them first from Fulfillment Lifecycle."],
  },

  "cohorts": {
    title: "Cohort Dashboard",
    route: "/cohorts",
    screenshot: "cohorts.png",
    purpose: "Pre-built cohort lenses (e.g. New Joiners, At-Risk, Champions, Dormant) that update nightly and feed into Campaigns.",
    audience: "Manager roles.",
    sections: [
      sec("Cohort Tiles", "Six tiles each showing count, 30-day delta, and 'View Members' link.", [
        "Champions — top 10% by Q-to-date order value.",
        "Rising Stars — physios whose order velocity > 2x previous 30 days.",
        "At Risk — no order in 21+ days, was previously active.",
        "Dormant — no order in 60+ days.",
        "New Joiners — onboarded within the last 14 days.",
        "Milestone Close — within 10% of the next milestone slab.",
      ]),
      sec("Members Drawer", "Click a tile → side drawer with the physio list, exportable as CSV.", []),
      sec("Push to Campaign", "Top-right button — pipes the cohort into the Campaign Manager as a pre-filled segment.", []),
    ],
    tips: ["Cohorts refresh every 6 hours; use the refresh button on a tile to recompute on demand."],
  },

  "campaigns": {
    title: "Campaign Manager · Segment Builder",
    route: "/campaigns",
    screenshot: "campaigns.png",
    purpose: "Design targeted broadcasts. The Segment Builder lets you compose nested AND / OR rules across 40+ physio attributes, then push the segment to a WhatsApp template, SMS or in-app message.",
    audience: "Manager roles.",
    sections: [
      sec("Campaigns Table", "Existing campaigns with: Name, Channel (WhatsApp / SMS / Email), Segment, Status (Draft / Scheduled / Sent), Reach, CTR.", []),
      sec("Create Campaign Wizard", "Step 1: Name + Channel. Step 2: Segment Builder (see below). Step 3: Template + Variables. Step 4: Schedule (Send now / Date-time).", []),
      sec("Segment Builder — Group Node", "Pick AND / OR at the group level. Add rules or nested groups. Nest as deep as needed — the preview button shows the matching count live.", []),
      sec("Segment Builder — Available Fields", "40+ fields grouped: Profile (state, city, status), Activity (last order date, orders count, AOV), Wallet (earned points, redeemed), Hierarchy (DSE, region, zone), Milestones, Tags.", []),
      sec("Segment Builder — Operators", "By field type: equals · not equals · in · not in · greater than · less than · between · contains · is empty · is not empty.", []),
      sec("Preview Pane", "Live count of matched physios + the first 25 sample rows. Updates every 600ms while you edit.", []),
      sec("Save Segment", "Save the rule tree as a named segment to reuse in future campaigns.", []),
    ],
    tips: [
      "If the 'Add Filter' drop-down looks empty, hard-refresh the browser — the schema is cached.",
      "Use 'in' / 'not in' instead of multiple equals — it produces faster queries.",
    ],
  },

  "whatsapp": {
    title: "WhatsApp Studio",
    route: "/whatsapp",
    screenshot: "whatsapp.png",
    purpose: "Author and test WhatsApp templates (which require Meta approval) and view inbound message logs from the Qikchat provider.",
    audience: "Manager roles.",
    sections: [
      sec("Templates Library", "All approved + draft templates. Each row: name, language, status (Approved / Pending / Rejected by Meta).", []),
      sec("Create Template", "Form fields: Name (lowercase, underscores), Language (en_IN / hi / mr / ta / te), Category (Marketing / Utility / Authentication), Body with placeholders {{1}} {{2}}, optional Header/Footer/Buttons.", []),
      sec("Test Send", "Send a template to a single number for visual QA before submitting to Meta or scheduling a campaign.", []),
      sec("Inbound Log", "Last 100 messages received from physios — useful for debugging onboarding flows and reward picks.", []),
    ],
    tips: ["Meta typically approves marketing templates in 1–2 hours; utility templates in ~10 minutes."],
  },

  /* ==================== OPERATIONS ==================== */
  "inventory": {
    title: "Inventory & Fulfillment",
    route: "/inventory",
    screenshot: "inventory.png",
    purpose: "Track physical stock of every redeemable reward across warehouses. Adjust counts, transfer stock between locations, and view low-stock alerts.",
    audience: "Manager roles.",
    sections: [
      sec("Stock Table", "Columns: Reward, SKU, Warehouse, On-Hand, Reserved, Available, Threshold, Last Movement.", []),
      sec("Warehouse Filter", "Drop-down — defaults to All. Showing per-warehouse counts.", []),
      sec("Stock Adjustment", "Modal: +/- units, reason (Stock In · Damage · Audit Correction · Transfer), reference doc id (optional).", []),
      sec("Transfer", "Move stock from Warehouse A to B; creates two linked movements.", []),
    ],
    tips: ["'Available' = On-Hand − Reserved. Reservations live in Fulfillment Lifecycle."],
  },

  "fulfillment": {
    title: "Fulfillment Lifecycle",
    route: "/fulfillment",
    screenshot: "fulfillment.png",
    purpose: "Every redemption flows through this queue: Reserved → Picked → Packed → Shipped → Delivered. Operators advance the state and attach proof.",
    audience: "Fulfillment operators, Managers.",
    sections: [
      sec("Pipeline Columns", "Kanban-style: Reserved / Picked / Packed / Shipped / Delivered / Cancelled. Drag a card right to advance.", []),
      sec("Card Detail", "Click any card to see: physio, reward, points debited, address, courier (if shipped), POD image upload.", []),
      sec("Bulk Actions", "Tick multiple cards in 'Packed' → Mark all 'Shipped' with a single AWB upload.", []),
      sec("Status Filters", "Top bar filters: status, courier, date range.", []),
    ],
    tips: ["SLA clock starts at 'Reserved'. Anything past the configured SLA shows a red badge."],
  },

  "alerts": {
    title: "48-Hour Alerts",
    route: "/alerts",
    screenshot: "alerts.png",
    purpose: "Lists physios who have raised an issue or whose redemption has stalled longer than the 48-hour SLA window — your hot-list for the day.",
    audience: "All admin roles.",
    sections: [
      sec("Alert Table", "Type, Physio, Raised At, Owner, Age (hours), Status.", []),
      sec("Type Filter", "Order Not Confirmed · Reward Reserved Too Long · Grievance Unanswered · Onboarding Stalled.", []),
      sec("Assign", "Drop-down to assign an owner. Triggers a WhatsApp ping to that owner.", []),
      sec("Resolve", "Marks the alert closed and logs the resolution note to Audit.", []),
    ],
    tips: ["Alerts auto-close when the underlying condition is fixed (e.g. order is confirmed)."],
  },

  "grievances": {
    title: "Grievance Desk",
    route: "/grievances",
    screenshot: "grievances.png",
    purpose: "Customer-service ticketing for any physio complaint — wrong reward, missing points, KYC issue, delivery failure.",
    audience: "All admin roles.",
    sections: [
      sec("Ticket Table", "Columns: Ticket #, Physio, Category, Priority, Owner, Age, Status (Open / In Progress / Resolved / Closed).", []),
      sec("Create Ticket", "Modal — Category drop-down: Points · Reward · Delivery · KYC · Other. Description + attachments.", []),
      sec("Reply Thread", "Each ticket has a chronological message thread. Replies can be sent as WhatsApp or internal note.", []),
      sec("SLA Badge", "Auto-calculated by priority: P1 = 4h, P2 = 24h, P3 = 72h.", []),
    ],
    tips: ["Bulk-resolve closed tickets monthly to keep the queue clean."],
  },

  "audit": {
    title: "Compliance & Audit",
    route: "/audit",
    screenshot: "audit.png",
    purpose: "Immutable, append-only log of every consequential action on the platform. Required for pharma compliance audits (UCPMP).",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("Event Stream", "Columns: Timestamp, Actor, Action, Entity, Before → After diff (JSON).", []),
      sec("Action Filter", "Drop-down: Login · Hierarchy Change · Manual Adjustment · Reward Approval · Bulk Import · Wipe Data.", []),
      sec("Date Range", "Calendar input to narrow the log.", []),
      sec("Export", "CSV / JSON export — used to hand to compliance officers.", []),
    ],
    tips: ["Audit events can never be edited or deleted — even Super Admin cannot tamper with them."],
  },

  /* =================== ADMINISTRATION =================== */
  "admin-imports": {
    title: "Data Imports",
    route: "/admin/imports",
    screenshot: "admin-imports.png",
    purpose: "Bulk-load production data — Physio Master, Outlet Hierarchy, Product Catalog, Orders, Wallet adjustments. Handles tens of thousands of rows.",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("Import Type Selector", "Drop-down — Physio Master · Outlet Hierarchy · Product Catalog · Orders · Wallet Adjustments.", []),
      sec("Template Download", "Each type has a sample CSV/XLSX with the exact column names expected.", []),
      sec("Upload + Validate", "Drag a file; the system parses and shows a preview with errors highlighted in red. Fix and re-upload, or proceed with valid rows only.", []),
      sec("Commit", "Final step — writes to the database. A progress bar shows row-by-row status; you can leave the page and come back, the job continues in the background.", []),
      sec("History", "Table of past imports with status (Success / Partial / Failed), row counts and downloadable error log.", []),
    ],
    tips: [
      "Always test a 10-row file first on a fresh tenant before importing the full master.",
      "tenant_id and ERP Outlet Id MUST match your current tenant — otherwise the rows look 'invisible' after import.",
    ],
  },

  "users": {
    title: "User Management",
    route: "/users",
    screenshot: "users.png",
    purpose: "Create, edit and block / unblock platform users (the admin / manager / DSE accounts). Distinct from physios.",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("User Table", "Name, Email, Role, Reports To, Territory, Status, Last Login.", []),
      sec("Add User", "Modal: Name, Email, Role (drop-down restricts to roles your scope can create), Reports To (auto-filtered), Territory label, temporary password.", []),
      sec("Role Drop-down", "Super Admin · HO Admin · National Manager · Zonal Manager · Regional Manager · DSE.", []),
      sec("Block / Unblock (lock icon)", "Toggle login access without deleting the user. Blocking prompts for a reason. A blocked user gets 'Account disabled' on every login attempt (OTP, ERP and email). Unblocking restores access. Both actions are recorded in Compliance & Audit.", []),
      sec("Status Pill", "Green 'active' = can log in. Rose 'blocked' = login disabled.", []),
      sec("Delete (trash icon)", "Permanently removes the user. Cannot be undone — prefer Block for reversible actions.", []),
    ],
    tips: ["Use Block (not Delete) whenever you want to revoke access — historical ownership records stay intact for audit."],
  },

  "tenants": {
    title: "Tenant Management",
    route: "/tenants",
    screenshot: "tenants.png",
    purpose: "Super-Admin only. Manage the multi-tenant fabric — add a new brand (e.g. another Jenburkt product line), configure branding, and view per-tenant usage.",
    audience: "Super Admin only.",
    sections: [
      sec("Tenant Table", "Name, Slug, Created, Physios, Active Users, Status.", []),
      sec("Add Tenant", "Modal: Name, Slug (URL-safe), Brand colour, Logo upload, Default points-per-rupee.", []),
      sec("Switch Tenant", "Use the tenant pill in the top bar to log into a tenant context.", []),
      sec("Tenant Quotas", "Per-tenant caps on physios / messages / storage. Defaults are unlimited for paid plans.", []),
    ],
    tips: ["Slugs are immutable — pick carefully."],
  },

  "config": {
    title: "Configuration",
    route: "/config",
    screenshot: "config.png",
    purpose: "Programme-wide settings — points engine, message providers, branding, danger-zone (wipe data).",
    audience: "Super Admin, HO Admin, National Manager.",
    sections: [
      sec("Points Engine", "Fields: Default points-per-rupee, Earned wallet quarter-reset (on/off), Minimum-order-value to award points, Maximum points per single order (anti-fraud cap).", []),
      sec("WhatsApp Provider", "Qikchat API key + sender id. Test message button at the bottom.", []),
      sec("SMS Provider", "Karix API key + sender id. Same test pattern.", []),
      sec("Branding", "Logo, primary colour, e-mail from-name. Reflected across the app.", []),
      sec("Danger Zone — Wipe Data", "Red panel at the bottom. Confirmation modal requires typing 'WIPE' to proceed. Clears physios, transactions, campaigns, segments. Preserves users + tenants. Use only when prepping for a fresh production launch.", []),
    ],
    tips: ["The Wipe Data action is logged in Audit with the actor and timestamp — there is no rollback."],
  },
};

export function moduleLookup(slug) {
  return TRAINING_MODULES[slug] || null;
}
